import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { Users, Trash2, Loader2, AlertCircle, ShieldCheck, Mail, Phone, RefreshCw, Wand2, Crown } from "lucide-react";

/**
 * v23.4 — Admin: find & resolve duplicate accounts.
 * Lists groups of users that share an email OR normalized phone, with delete
 * actions per row so admin can keep the earliest verified record and nuke the rest.
 */
export default function AdminDuplicatesPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [deletingId, setDeletingId] = React.useState(null);
  const [mergingGroup, setMergingGroup] = React.useState(null);
  const [mergeReport, setMergeReport] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await apiClient.get("/admin/duplicates");
      setData(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load duplicates");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const removeUser = async (uid, email) => {
    if (!window.confirm(`Delete user ${email}? This removes their account, driver documents, and reset tokens. Job history is preserved.`)) return;
    setDeletingId(uid);
    try {
      await apiClient.delete(`/admin/users/${uid}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const autoMerge = async (group) => {
    const winnerId = group.suggested_winner_id;
    const winnerUser = group.users.find((u) => u.id === winnerId);
    const losers = group.users.filter((u) => u.id !== winnerId);
    if (!winnerUser || losers.length === 0) return;
    const msg = `Auto-merge ${losers.length} duplicate${losers.length === 1 ? "" : "s"} into "${winnerUser.email}"?\n\nTHE WINNER will receive: jobs, ratings, driver documents, Stripe IDs, subscription tier, missing phone — from every loser.\n\nThe ${losers.length} loser account${losers.length === 1 ? "" : "s"} will then be DELETED. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    const key = `${group.key}-${group.value}`;
    setMergingGroup(key);
    setMergeReport(null);
    try {
      const { data: r } = await apiClient.post("/admin/users/merge", {
        winner_id: winnerId,
        loser_ids: losers.map((u) => u.id),
      });
      setMergeReport({ key, ...r });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Auto-merge failed");
    } finally {
      setMergingGroup(null);
    }
  };

  return (
    <section className="mb-12" data-testid="admin-duplicates-section">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight text-[#0A0A0A] flex items-center gap-2">
            <Users size={16} className="text-[#1E4D55]" /> Duplicate accounts
          </h3>
          <p className="text-xs text-[#475569] max-w-xl">
            Users that share an email or phone. Keep the earliest verified record (top of each group) and delete the rest. New signups with a matching email or phone are now <strong>blocked at registration</strong>.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="duplicates-refresh-btn"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E4D55] hover:text-[#0A0A0A]"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-xs text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="duplicates-error">
          <AlertCircle size={12} className="mt-0.5"/> {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-xs text-[#475569] inline-flex items-center gap-2">
          <Loader2 size={12} className="animate-spin"/> Scanning&hellip;
        </div>
      )}

      {data && data.total_groups === 0 && (
        <div className="bg-[#5BB247]/10 border border-[#5BB247]/30 p-4 text-sm text-[#1f5d2e] inline-flex items-center gap-2" data-testid="duplicates-clean">
          <ShieldCheck size={16}/> No duplicates found — every email and phone in the system is unique.
        </div>
      )}

      {data && data.total_groups > 0 && (
        <div className="space-y-4" data-testid="duplicates-groups">
          <p className="text-xs text-[#475569]">
            <strong>{data.total_groups}</strong> duplicate group{data.total_groups === 1 ? "" : "s"} found.
          </p>
          {data.duplicates.map((g) => {
            const groupKey = `${g.key}-${g.value}`;
            const isMerging = mergingGroup === groupKey;
            const showReport = mergeReport && mergeReport.key === groupKey;
            return (
            <div key={groupKey} data-testid={`dup-group-${g.key}-${g.value}`} className="bg-white border border-[#0A0A0A]/10">
              <div className="bg-[#F5F5F5] px-4 py-3 border-b border-[#0A0A0A]/10 flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs font-mono text-[#475569]">
                  {g.key === "email" ? <Mail size={12} className="inline mr-1.5 mb-0.5"/> : <Phone size={12} className="inline mr-1.5 mb-0.5"/>}
                  {g.key.toUpperCase()}: <span className="text-[#0A0A0A] font-bold">{g.value}</span>
                </p>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[#B91C1C]">{g.count} accounts</span>
                  {g.suggested_winner_id && g.users.length > 1 && (
                    <button
                      onClick={() => autoMerge(g)}
                      disabled={isMerging}
                      data-testid={`dup-automerge-${g.key}-${g.value}`}
                      className="inline-flex items-center gap-1.5 bg-[#5BB247] hover:bg-[#4A9939] text-[#0A0A0A] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider disabled:opacity-60"
                      title={`Merge ${g.count - 1} losers into the suggested winner`}
                    >
                      {isMerging ? <Loader2 size={12} className="animate-spin"/> : <Wand2 size={12}/>}
                      Auto-merge into winner
                    </button>
                  )}
                </div>
              </div>

              {showReport && (
                <div className="bg-[#5BB247]/10 border-b border-[#5BB247]/30 px-4 py-2 text-[11px] text-[#1f5d2e]" data-testid={`merge-report-${g.key}-${g.value}`}>
                  ✓ Merged {mergeReport.losers_processed} loser{mergeReport.losers_processed === 1 ? "" : "s"}.
                  {mergeReport.report?.length > 0 && (() => {
                    const totals = mergeReport.report.reduce((acc, r) => {
                      const m = r.merged || {};
                      acc.jobs += (m.jobs_as_customer || 0) + (m.jobs_as_driver || 0) + (m.jobs_as_payer || 0);
                      acc.quotes += m.quotes || 0;
                      acc.ratings += (m.ratings_given || 0) + (m.ratings_received || 0);
                      acc.docs += m.driver_documents || 0;
                      return acc;
                    }, { jobs: 0, quotes: 0, ratings: 0, docs: 0 });
                    const bits = [];
                    if (totals.jobs) bits.push(`${totals.jobs} job${totals.jobs === 1 ? "" : "s"}`);
                    if (totals.quotes) bits.push(`${totals.quotes} quote${totals.quotes === 1 ? "" : "s"}`);
                    if (totals.ratings) bits.push(`${totals.ratings} rating${totals.ratings === 1 ? "" : "s"}`);
                    if (totals.docs) bits.push(`${totals.docs} doc${totals.docs === 1 ? "" : "s"}`);
                    return bits.length ? ` Transferred: ${bits.join(" · ")}.` : "";
                  })()}
                </div>
              )}

              <table className="w-full text-xs">
                <thead className="text-[#475569]">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Email</th>
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-left font-semibold">Phone</th>
                    <th className="px-3 py-2 text-left font-semibold">Role</th>
                    <th className="px-3 py-2 text-left font-semibold">Signals</th>
                    <th className="px-3 py-2 text-left font-semibold">Score</th>
                    <th className="px-3 py-2 text-left font-semibold">Created</th>
                    <th className="px-3 py-2 text-right font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {g.users.map((u) => {
                    const isWinner = u.id === g.suggested_winner_id;
                    return (
                    <tr key={u.id} className={isWinner ? "bg-[#5BB247]/10" : "border-t border-[#0A0A0A]/5"} data-testid={`dup-row-${u.id}`}>
                      <td className="px-3 py-2 font-mono text-[#0A0A0A] break-all">{u.email}</td>
                      <td className="px-3 py-2 text-[#0A0A0A]">{u.name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[#475569]">{u.phone || "—"}</td>
                      <td className="px-3 py-2 text-[#475569]">{u.role}</td>
                      <td className="px-3 py-2 text-[#475569] whitespace-nowrap">
                        {u.email_verified && <span title="Email verified" className="text-[#2c8642] mr-1">✉︎✓</span>}
                        {u.verified && <span title="KYC verified" className="text-[#2c8642] mr-1">★</span>}
                        {u.has_stripe_account && <span title="Stripe Connect onboarded" className="text-[#1E4D55] mr-1">£</span>}
                        {u.has_stripe_customer && <span title="Stripe customer" className="text-[#1E4D55] mr-1">+</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-[#475569]">
                        {isWinner ? (
                          <span className="inline-flex items-center gap-1 text-[#2c8642] font-bold" data-testid={`dup-winner-${u.id}`}>
                            <Crown size={10}/> {u.merge_score}
                          </span>
                        ) : u.merge_score}
                      </td>
                      <td className="px-3 py-2 text-[#475569]">{u.created_at ? u.created_at.slice(0, 10) : "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => removeUser(u.id, u.email)}
                          disabled={deletingId === u.id || isMerging}
                          data-testid={`dup-delete-${u.id}`}
                          className="inline-flex items-center gap-1 text-[#B91C1C] hover:text-[#7f1414] font-semibold disabled:opacity-50"
                        >
                          {deletingId === u.id ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>}
                          Delete
                        </button>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          );})}
        </div>
      )}
    </section>
  );
}
